// Packages
import {markRaw} from 'vue';
import {createStore} from 'vuex';
import {decompressBase64ToObject} from '@northpoint-development/labs-common-core';

// Services and Helpers
import {baseState, baseMutations, baseActions, baseGetters} from '@/store/baseIndex';
import collections from './collections.js';
import infowindow from './infowindow.js';
import layers from './layers.js';
import toolbox from './toolbox.js';
import {currentUser} from '@/services/FirestoreService';
import {SharedMap} from '@/lib/classes/SharedMap';
import {getAddableFeatures} from '@/services/OverlaysService';

// Components
import LocalSearchBar from '@/external/components/LocalSearchBar.vue';
import LayersMenu from '@/components/LayersMenu/LayersMenu.vue';
import SharedItemMenu from '@/external/components/SharedItemMenu/SharedItemMenu.vue';
import ToolboxMenu from '@/components/Toolbox/ToolboxMenu.vue';
import DeleteSharedMap from '@/components/DeleteSharedMap.vue';

/**
 * ___Type Imports___
 * @typedef {import('vuex').MutationPayload} MutationPayload
 * @typedef {import('vuex').ActionContext} ActionContext
 * @typedef {import('vuex').ActionPayload} ActionPayload
 * @typedef {import('@/store/baseIndex').AppState} AppState
 * @typedef {import('@/lib/classes/SharedMap.js').SharedMap} SharedMap
 * @typedef {import('@/services/OverlaysService').Feature} Feature
 *
 * @typedef {Object} ExternalState
 * // BUG: allowEdit is defined in this typedef, but not in the state object and it is not set by any mutations/actions
 * @property {boolean} allowEdit
 * @property {string} mapId
 * @property {SharedMap} sharedMap
 * @property {Map<string, Feature>} featuresForGrid
 * @property {string} featureGridFilter
 * @property {string} dataTitle
 *
 * @typedef {AppState & ExternalState} ExternalAppState
 *
 * @typedef {Object} RoutePayload
 * @property {string} mode
 * @property {string} id
 */

/** @type {ExternalAppState} */
const state = Object.assign(baseState, {
  configCollectionName: 'app-config',
  subMenus: [
    {id: 'search', tip: 'Search', component: markRaw(LocalSearchBar), type: 'bar'},
    {id: 'toolbox', tip: 'Tools', component: markRaw(ToolboxMenu), type: 'bar', keepAlive: true},
    {id: 'sharedItemMenu', tip: 'Features', component: markRaw(SharedItemMenu), type: 'sheet', keepAlive: true},
    {id: 'layers', tip: 'Layers', component: markRaw(LayersMenu), type: 'sheet', keepAlive: true},
  ],
  isEditableMap: false,
  hasLayersCatalog: false,
  mapId: null,
  sharedMap: null,
  recentlyDeletedMapIds: [],
  addableFeatures: null, // AddFeatureButton initializes these values from `refreshAddableFeatures`
  canImportImagesAtAnyZoom: true,
  allowFolderMarkerIcons: true,
  allowFeatureGrid: false, // default to false, but this should track isEditableMap
  featuresForGrid: null,
  featureGridFilter: null,
  dataTitle: null,
});

const mutations = Object.assign(baseMutations, {
  /** Sets the feature grid parent for the app. */
  setFeatureGrid: (s, p) => {
    s.featuresForGrid = p.features;
    s.featureGridFilter = p.featureType || null;
  },
  /**
   * @param {ExternalAppState} s state object
   * @param {boolean} p the mutation payload as an edit boolean
   */
  setIsEditableMap: (s, p) => {
    s.isEditableMap = p;
    s.allowFeatureGrid = p;
  },
  /**
   * This function is used when edit mode changes to true and the menus needs to include a share button
   * @param {ExternalAppState} s state object
   * @param {SubMenu} p the mutation payload as a SubMenu object
   */
  addSubMenu: (s, p) => {
    // remove submenu if it already exists
    s.subMenus = s.subMenus?.filter((subMenu) => subMenu.id !== p.id);
    s.subMenus?.push(p);
  },
  /**
   * This function sets the current shared map
   * @param {ExternalAppState} s state object
   * @param {SharedMap} p the mutation payload as a SharedMap object
   */
  setSharedMap: (s, p) => {
    s.mapId = p.id;
    s.sharedMap = new SharedMap(p);
    s.dataTitle = s.sharedMap.name;
  },
  /**
   * Resets the store's Shared Map to its initial state.
   * @param {ExternalAppState} s state object
   */
  clearSharedMap: (s) => {
    s.mapId = null;
    s.sharedMap = null;
    s.isEditableMap = false;
    s.subMenus = [
      {id: 'search', tip: 'Search', component: markRaw(LocalSearchBar), type: 'bar'},
      {id: 'toolbox', tip: 'Tools', component: markRaw(ToolboxMenu), type: 'bar', keepAlive: true},
      {id: 'sharedItemMenu', tip: 'Features', component: markRaw(SharedItemMenu), type: 'sheet', keepAlive: true},
      {id: 'layers', tip: 'Layers', component: markRaw(LayersMenu), type: 'sheet', keepAlive: true},
    ];
  },
  /**
   * This function is used when a map is deleted and the mapId needs to be added to the recentlyDeletedMapIds array
   * @param {ExternalAppState} s state object
   * @param {SubMenu} p the mutation payload as a string
   */
  addRecentlyDeletedMapId: (s, p) => {
    s.recentlyDeletedMapIds = [...s.recentlyDeletedMapIds, p];
  },
  /**
   * refreshes the addable features when the zoom level changes
   * @param {ExternalAppState} s state object
   * @param {number} zoomLevel the current zoom level
   */
  refreshAddableFeatures: (s, zoomLevel) => {
    /** @type {import('@/store/baseIndex').AddableFeature[]} */
    const addableFeatures = [
      {type: 'png', ignoreZoomLevel: true},
      {type: 'blockPlan', ignoreZoomLevel: false},
      {type: 'polygon', ignoreZoomLevel: true},
      {type: 'route', ignoreZoomLevel: true},
      {type: 'label', ignoreZoomLevel: true},
      {type: 'placeMarker', ignoreZoomLevel: true},
      {type: 'folder', ignoreZoomLevel: true},
    ];
    s.addableFeatures = getAddableFeatures(addableFeatures, zoomLevel);
  },
});

const actions = Object.assign(baseActions, {
  /**
   * @param {ActionContext} context Vuex context object
   * @param {RoutePayload} payload Vuex action payload
   */
  setupMap: async ({commit}, {mode, id, map, isAuthorizedUser = false}) => {
    // Handle edit mode
    const editMode = mode === 'edit';
    if (editMode) {
      commit('setIsEditableMap', true);
      commit('addSubMenu', {id: 'trash', tip: 'Trash', component: markRaw(DeleteSharedMap)});
      commit('addSubMenu', {id: 'NPEditorShare', tip: 'Share', component: null});
      commit('addSubMenu', {id: 'dashboard', tip: 'Home', component: null});
    } else {
      // Only show the Share button for authorized users
      if (isAuthorizedUser) {
        // We want a different share icon for view mode
        commit('addSubMenu', {id: 'NPViewerShare', tip: 'Share', component: null});
      }
      commit('addSubMenu', {id: 'OrangeCircle', tip: '', component: null, clickable: false});
    }

    map.folders = await decompressBase64ToObject(map.folders);

    commit('setSharedMap', {id, editMode, ...map});
  },
});

const getters = Object.assign(baseGetters, {
  userCanEditMap: (state) => {
    const uid = currentUser().uid;
    return state.sharedMap.createdOn?.uid === uid ||
      state.sharedMap.share?.accessLevel === 'edit' ||
      (state.sharedMap.share?.users?.includes(uid) ?? false);
  },
});

const initializeStore = () => {
  return createStore({
    state,
    actions,
    mutations,
    getters,
    modules: {
      collections,
      infowindow,
      layers,
      toolbox,
    },
  });
};

export {
  initializeStore,
  // Testing only
  state,
  mutations,
  actions,
};
